import React from "react";

/**
 * Titleize a string.
 *
 * For example:
 *
 *    "foo_bar" => "Foo Bar"
 *
 * @param s string
 * @return titleized string
 */
function titleize(s="") {
  return s
    ? s.replace(/_/g, ' ').replace(/\b[a-z]/g, m => m.toUpperCase())
    : s;
}

/**
 * Conjunct a set of two or more nouns with commas and stuff.  Oxford comma included.
 *
 * For example:
 *  conjunct(['eats', 'shoots', 'leaves']) => "eats, shoots, and leaves"
 *
 *  conjunct(['shoots', 'leaves']) => "shoots and leaves"
 *
 * specify conjunction if you want something other than and:
 *  conjunct(['ifs', 'ands', 'butts'], 'or') => "ifs, ands, or butts"
 *
 * @param items         array of items to be conjuncted
 * @param conjunction   conjuncting word to use before last item (i.e. 'and' or 'or').  Default 'and'.
 * @param delimiter     character to use between each item (i.e. ',' or ';').  Default ','.
 */
function conjunct(items, conjunction='and', delimiter=', ') {
  const addConjunction = (item, index) => {
    // zero or one items, no need to conjoin
    if (items.length < 2){
      return item;
    }

    // not the last item in the list, so just return it
    if (index < items.length-1){
      return item;
    }

    // last item, so prepend the conjoiner
    return `${conjunction} ${item}`;
  };

  if (items.length === 2) {
    return items.map(addConjunction).join(' ');
  }

  return items.map(addConjunction).join(delimiter);
}

/**
 * Detects if a string contains non-whitespace characters.
 *
 * @param string string to test
 * @return true if string is present
 */
const present = (string) => (string !== null) && (string !== undefined) && (string.trim().length > 0);

/**
 * Pluralize a string
 *
 * @param count     How many
 * @param singular  Singular word (i.e. "Lone Ranger")
 * @param plural    Word to use when it's plural, i.e. `pluralize(count, 'man', {plural: 'men'}`).  Default is singular plus s
 *
 * @returns {string}
 */
const pluralize = (count, singular, plural = null) => {
  const isPlural = Math.abs(count) != 1;

  return isPlural ? (plural || singular + 's') : singular;
};

/**
 * Truncates a string and appends the suffix '...'. By default truncates strings exceeding 30 characters so
 * that the new string is 30 characters including suffix.
 *
 * @param {string} string the string to truncate
 * @param {object} options optional parameters
 * @param {number} options.length the maximum string length
 * @returns {*|string} truncated string
 */
const truncate = (string, options = {}) => {
  const {length = 30} = options;

  if (string && string.length > length) {
    return string.slice(0, length - 3) + '...';
  } else {
    return string;
  }
};

export {
  conjunct,
  pluralize,
  present,
  titleize,
  truncate
};


